import { useState, useEffect } from 'react';

interface Collaborator {
  id: string;
  name: string;
  avatar: string;
  color: string;
  online: boolean;
}

interface Annotation {
  id: string;
  author: Collaborator;
  pinNumber: number;
  comment: string;
  timestamp: string;
  resolved: boolean;
  replies: Reply[];
  expanded: boolean;
}

interface Reply {
  id: string;
  author: Collaborator;
  text: string;
  timestamp: string;
}

interface VersionSnapshot {
  id: string;
  name: string;
  timestamp: string;
  author: string;
}

interface GhostCursor {
  id: string;
  name: string;
  color: string;
  x: number;
  y: number;
}

const mockCollaborators: Collaborator[] = [
  { id: '1', name: 'Sarah Chen', avatar: 'SC', color: '#10B981', online: true },
  { id: '2', name: 'Mike Johnson', avatar: 'MJ', color: '#3B82F6', online: true },
  { id: '3', name: 'Emma Davis', avatar: 'ED', color: '#F59E0B', online: true },
];

const mockVersions: VersionSnapshot[] = [
  { id: 'v5', name: 'Final client review', timestamp: '2 hours ago', author: 'Sarah Chen' },
  { id: 'v4', name: 'Kitchen layout update', timestamp: '5 hours ago', author: 'Mike Johnson' },
  { id: 'v3', name: 'Added master suite', timestamp: '1 day ago', author: 'Emma Davis' },
  { id: 'v2', name: 'Initial floor plan', timestamp: '2 days ago', author: 'Sarah Chen' },
  { id: 'v1', name: 'Project kickoff', timestamp: '3 days ago', author: 'Mike Johnson' },
];

interface CollaborationPanelProps {
  darkMode: boolean;
}

export default function CollaborationPanel({ darkMode }: CollaborationPanelProps) {
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([
    {
      id: 'a1',
      author: mockCollaborators[0],
      pinNumber: 1,
      comment: 'Should we expand the master bedroom by 2 meters?',
      timestamp: '2 min ago',
      resolved: false,
      replies: [
        {
          id: 'r1',
          author: mockCollaborators[1],
          text: 'Good idea, but check if it affects the bathroom layout',
          timestamp: '1 min ago',
        },
      ],
      expanded: false,
    },
    {
      id: 'a2',
      author: mockCollaborators[1],
      pinNumber: 2,
      comment: 'Kitchen island needs more clearance on the left side',
      timestamp: '5 min ago',
      resolved: false,
      replies: [],
      expanded: false,
    },
    {
      id: 'a3',
      author: mockCollaborators[2],
      pinNumber: 3,
      comment: 'Love the open floor plan concept!',
      timestamp: '12 min ago',
      resolved: true,
      replies: [],
      expanded: false,
    },
  ]);
  const [newComment, setNewComment] = useState('');
  const [replyTexts, setReplyTexts] = useState<{ [key: string]: string }>({});
  const [unreadCount, setUnreadCount] = useState(2);
  const [isCanvasLocked, setIsCanvasLocked] = useState(false);
  const [showInviteToast, setShowInviteToast] = useState(false);
  const [showVersionToast, setShowVersionToast] = useState(false);
  const [versionToastMessage, setVersionToastMessage] = useState('');
  const [ghostCursors, setGhostCursors] = useState<GhostCursor[]>([
    { id: 'gc1', name: 'Sarah Chen', color: '#10B981', x: 30, y: 40 },
    { id: 'gc2', name: 'Mike Johnson', color: '#3B82F6', x: 60, y: 70 },
  ]);

  const tools = [
    { id: 'pin', icon: 'ri-pushpin-line', label: 'Pin comment', color: '#EF4444' },
    { id: 'draw', icon: 'ri-pencil-line', label: 'Draw freehand', color: '#8B5CF6' },
    { id: 'text', icon: 'ri-text', label: 'Add text label', color: '#3B82F6' },
    { id: 'measure', icon: 'ri-ruler-line', label: 'Measure distance', color: '#10B981' },
    { id: 'highlight', icon: 'ri-mark-pen-line', label: 'Highlight area', color: '#F59E0B' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setGhostCursors((prev) =>
        prev.map((cursor) => ({
          ...cursor,
          x: Math.max(10, Math.min(90, cursor.x + (Math.random() - 0.5) * 20)),
          y: Math.max(10, Math.min(90, cursor.y + (Math.random() - 0.5) * 20)),
        }))
      );
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const handlePostComment = () => {
    if (!newComment.trim()) return;

    const newAnnotation: Annotation = {
      id: `a${Date.now()}`,
      author: { id: 'current', name: 'You', avatar: 'YO', color: '#6366F1', online: true },
      pinNumber: annotations.length + 1,
      comment: newComment,
      timestamp: 'Just now',
      resolved: false,
      replies: [],
      expanded: false,
    };

    setAnnotations([newAnnotation, ...annotations]);
    setNewComment('');
  };

  const handleReply = (annotationId: string) => {
    const replyText = replyTexts[annotationId];
    if (!replyText?.trim()) return;

    setAnnotations((prev) =>
      prev.map((ann) => {
        if (ann.id === annotationId) {
          return {
            ...ann,
            replies: [
              ...ann.replies,
              {
                id: `r${Date.now()}`,
                author: { id: 'current', name: 'You', avatar: 'YO', color: '#6366F1', online: true },
                text: replyText,
                timestamp: 'Just now',
              },
            ],
          };
        }
        return ann;
      })
    );

    setReplyTexts({ ...replyTexts, [annotationId]: '' });
  };

  const handleResolve = (id: string) => {
    setAnnotations((prev) =>
      prev.map((ann) => (ann.id === id ? { ...ann, resolved: !ann.resolved } : ann))
    );
  };

  const handleDelete = (id: string) => {
    setAnnotations((prev) => prev.filter((ann) => ann.id !== id));
  };

  const handleToggleExpand = (id: string) => {
    setAnnotations((prev) =>
      prev.map((ann) => (ann.id === id ? { ...ann, expanded: !ann.expanded } : ann))
    );
  };

  const handleInvite = () => {
    navigator.clipboard.writeText('https://1world-ai.com/collab/abc123xyz');
    setShowInviteToast(true);
    setTimeout(() => setShowInviteToast(false), 3000);
  };

  const handleRestore = (versionId: string) => {
    setVersionToastMessage(`Version ${versionId} restored successfully!`);
    setShowVersionToast(true);
    setTimeout(() => setShowVersionToast(false), 3000);
  };

  const handlePreview = (versionId: string) => {
    setVersionToastMessage(`Previewing version ${versionId}…`);
    setShowVersionToast(true);
    setTimeout(() => setShowVersionToast(false), 3000);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Active Collaborators */}
      <div className={`p-4 border-b transition-colors duration-300 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center justify-between mb-3">
          <span className={`text-sm font-medium transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Active Collaborators</span>
          <button
            onClick={handleInvite}
            className="px-3 py-1.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-xs font-medium transition-all duration-300 whitespace-nowrap"
          >
            <i className="ri-user-add-line mr-1"></i>
            Invite
          </button>
        </div>
        <div className="flex gap-2">
          {mockCollaborators.map((collab) => (
            <div
              key={collab.id}
              className="relative group cursor-pointer"
              title={collab.name}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm"
                style={{ backgroundColor: collab.color }}
              >
                {collab.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 transition-colors duration-300 ${
                collab.online ? 'bg-green-500' : 'bg-gray-400'
              } ${darkMode ? 'border-gray-800' : 'border-white'}`}></div>
            </div>
          ))}
        </div>
      </div>

      {/* Annotation Toolbar */}
      <div className={`p-4 border-b transition-colors duration-300 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <span className={`text-sm font-medium mb-3 block transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Annotation Tools</span>
        <div className="flex gap-2">
          {tools.map((tool) => (
            <button
              key={tool.id}
              onClick={() => setActiveTool(activeTool === tool.id ? null : tool.id)}
              className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all whitespace-nowrap ${
                activeTool === tool.id
                  ? 'border-current bg-opacity-10'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              style={{
                borderColor: activeTool === tool.id ? tool.color : undefined,
                backgroundColor: activeTool === tool.id ? `${tool.color}15` : undefined,
              }}
              title={tool.label}
            >
              <i className={`${tool.icon} text-xl`} style={{ color: tool.color }}></i>
            </button>
          ))}
        </div>
        {activeTool && (
          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
            <i className="ri-information-line mr-1"></i>
            {tools.find((t) => t.id === activeTool)?.label} mode active
          </div>
        )}
      </div>

      {/* Canvas Lock Toggle */}
      <div className={`p-4 border-b transition-colors duration-300 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className={`${isCanvasLocked ? 'ri-lock-line' : 'ri-lock-unlock-line'} text-lg text-gray-600`}></i>
            <span className="text-sm font-medium text-gray-900">Canvas Lock</span>
          </div>
          <button
            onClick={() => setIsCanvasLocked(!isCanvasLocked)}
            className={`relative w-12 h-6 rounded-full transition-colors whitespace-nowrap ${
              isCanvasLocked ? 'bg-red-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                isCanvasLocked ? 'translate-x-6' : 'translate-x-0'
              }`}
            ></span>
          </button>
        </div>
        {isCanvasLocked && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
            <i className="ri-lock-line mr-1"></i>
            Canvas locked by Sarah Chen
          </div>
        )}
      </div>

      {/* Add Comment Form */}
      <div className={`p-4 border-b transition-colors duration-300 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Add Comment</h3>
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Type your comment here..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
          rows={3}
        />
        <button
          onClick={handlePostComment}
          className="mt-2 w-full px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-sm font-medium rounded-lg hover:from-teal-600 hover:to-cyan-700 transition-all whitespace-nowrap"
        >
          <i className="ri-send-plane-fill mr-2"></i>
          Post Comment
        </button>
      </div>

      {/* Annotations List */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">
            Annotations ({annotations.filter((a) => !a.resolved).length})
          </h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="space-y-3">
          {annotations.map((annotation) => (
            <div
              key={annotation.id}
              className={`p-3 rounded-lg border-2 transition-all ${
                annotation.resolved
                  ? 'border-gray-200 bg-gray-50 opacity-60'
                  : 'border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                  style={{ backgroundColor: annotation.author.color }}
                >
                  {annotation.author.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-900">
                      {annotation.author.name}
                    </span>
                    <span
                      className="px-1.5 py-0.5 rounded text-xs font-bold text-white"
                      style={{ backgroundColor: annotation.author.color }}
                    >
                      #{annotation.pinNumber}
                    </span>
                    <span className="text-xs text-gray-500">{annotation.timestamp}</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{annotation.comment}</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleResolve(annotation.id)}
                      className={`px-2 py-1 text-xs font-medium rounded whitespace-nowrap ${
                        annotation.resolved
                          ? 'bg-gray-200 text-gray-600'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      <i
                        className={`${
                          annotation.resolved ? 'ri-check-double-line' : 'ri-check-line'
                        } mr-1`}
                      ></i>
                      {annotation.resolved ? 'Resolved' : 'Resolve'}
                    </button>
                    <button
                      onClick={() => handleDelete(annotation.id)}
                      className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded hover:bg-red-200 whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>
                      Delete
                    </button>
                    {annotation.replies.length > 0 && (
                      <button
                        onClick={() => handleToggleExpand(annotation.id)}
                        className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded hover:bg-blue-200 whitespace-nowrap"
                      >
                        <i
                          className={`${
                            annotation.expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
                          } mr-1`}
                        ></i>
                        {annotation.replies.length} {annotation.replies.length === 1 ? 'Reply' : 'Replies'}
                      </button>
                    )}
                  </div>

                  {/* Replies */}
                  {annotation.expanded && annotation.replies.length > 0 && (
                    <div className="mt-3 pl-4 border-l-2 border-gray-300 space-y-2">
                      {annotation.replies.map((reply) => (
                        <div key={reply.id} className="flex items-start gap-2">
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                            style={{ backgroundColor: reply.author.color }}
                          >
                            {reply.author.avatar}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs font-semibold text-gray-900">
                                {reply.author.name}
                              </span>
                              <span className="text-xs text-gray-500">{reply.timestamp}</span>
                            </div>
                            <p className="text-xs text-gray-700">{reply.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply Input */}
                  {!annotation.resolved && (
                    <div className="mt-3 flex gap-2">
                      <input
                        type="text"
                        value={replyTexts[annotation.id] || ''}
                        onChange={(e) =>
                          setReplyTexts({ ...replyTexts, [annotation.id]: e.target.value })
                        }
                        placeholder="Write a reply..."
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        onClick={() => handleReply(annotation.id)}
                        className="px-3 py-1 bg-indigo-500 text-white text-xs font-medium rounded hover:bg-indigo-600 whitespace-nowrap"
                      >
                        Reply
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Version History */}
      <div className={`p-4 border-t transition-colors duration-300 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Version History</h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {mockVersions.map((version) => (
            <div
              key={version.id}
              className="p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-all"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900 truncate">{version.name}</h4>
                  <p className="text-xs text-gray-500">
                    {version.author} · {version.timestamp}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleRestore(version.id)}
                  className="flex-1 px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-medium rounded hover:from-green-600 hover:to-emerald-700 transition-all whitespace-nowrap"
                >
                  <i className="ri-refresh-line mr-1"></i>
                  Restore
                </button>
                <button
                  onClick={() => handlePreview(version.id)}
                  className="flex-1 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-medium rounded hover:from-blue-600 hover:to-indigo-700 transition-all whitespace-nowrap"
                >
                  <i className="ri-eye-line mr-1"></i>
                  Preview
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Toast Notification */}
      {showInviteToast && (
        <div className="fixed bottom-6 right-6 px-4 py-3 bg-green-500 text-white rounded-lg shadow-lg flex items-center gap-2 animate-fade-in z-50">
          <i className="ri-check-line text-xl"></i>
          <span className="text-sm font-medium">Invite link copied to clipboard!</span>
        </div>
      )}

      {showVersionToast && (
        <div className="fixed bottom-6 right-6 px-4 py-3 bg-gray-900 text-white rounded-lg shadow-lg flex items-center gap-2 z-50">
          <div className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-check-line text-xs"></i>
          </div>
          <span className="text-sm font-medium">{versionToastMessage}</span>
        </div>
      )}

      {/* Ghost Cursors Overlay (to be rendered on canvas) */}
      <div className="hidden">
        {ghostCursors.map((cursor) => (
          <div
            key={cursor.id}
            className="ghost-cursor"
            data-name={cursor.name}
            data-color={cursor.color}
            data-x={cursor.x}
            data-y={cursor.y}
          />
        ))}
      </div>
    </div>
  );
}